/**
 * Cognitive Stream Processor for CRAIG Electronic Parts Intelligence
 * Angular Service Implementation
 *
 * Based on the working sample/CognitiveStreamProcessor.ts
 * Consumes SSE streams and creates hierarchical state structure for
 * Chain of Thought UI with collapsible span groups.
 */

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

// ===== TYPE DEFINITIONS =====

export interface SpanInfo {
    span_id: string;
    span_name: string;
    span_kind: 'cognitive_node' | 'cognitive_tool' | 'cognitive_workflow';
    parent_span_id: string | null;
    response_id: string;
    depth: number;
}

export interface DomainContext {
    subdomain: 'signal' | 'insight' | 'executive';
    bounded_context:
        | 'request_processing'
        | 'part_extraction'
        | 'knowledge_retrieval'
        | 'response_generation'
        | 'classification_service';
    entity: 'request' | 'part' | 'classification' | 'response' | 'document' | 'conversation' | 'tool_execution';
    entity_id?: string;
}

export interface CognitiveMetadata {
    chain_of_thought: boolean;
    reasoning_step: boolean;
    cognitive_phase: string;
    execution_time_ms?: number;
    [key: string]: any;
}

export interface CognitiveEvent {
    // MLflow 3.0 ResponsesAgent fields
    type: string;
    event_category: 'cognitive' | 'response';
    schema_version: string;
    timestamp: number;

    // Content
    content: string;
    formatted_content: string;

    // Span hierarchy for UI organization
    span: SpanInfo;

    // Domain context for filtering
    domain?: DomainContext;

    // Cognitive metadata for Chain of Thought
    cognitive: CognitiveMetadata;

    // Node/Tool context
    node_context?: { node_name: string; execution_context: string };
    tool_context?: { tool_name: string; bounded_context: string };

    // Original SSE event
    chunk_id: number;
    item_id: string;
}

export interface ResponseEvent {
    type: 'response.llm.token';
    content: string;
    chunk_id: number;
    item_id: string;
    timestamp: number;
}

export interface SpanNode {
    // Span information
    span_id: string;
    span_name: string;
    span_kind: 'cognitive_node' | 'cognitive_tool' | 'cognitive_workflow';
    parent_span_id: string | null;
    depth: number;

    // Execution metrics
    start_time?: number;
    end_time?: number;
    execution_time_ms?: number;
    status: 'running' | 'completed' | 'error';

    // Domain context
    domain?: DomainContext;

    // Events within this span
    events: CognitiveEvent[];

    // Child spans (for Tool spans under Node spans)
    children: SpanNode[];

    // Summary info for UI
    event_count: number;
    reasoning_steps: string[];
    performance_summary?: {
        total_time_ms: number;
        step_count: number;
        avg_step_time_ms: number;
    };

    // UI state
    collapsed?: boolean;
}

export interface ResponseExecution {
    response_id: string;
    start_time: number;
    end_time?: number;
    status: 'streaming' | 'completed' | 'error';

    // Two sibling hierarchies
    cognitive_tree: SpanNode[]; // Chain of Thought hierarchy
    response_content: ResponseEvent[]; // Final response tokens

    // Summary metrics
    total_cognitive_time_ms: number;
    total_events: number;
    nodes_executed: string[];
    tools_executed: string[];
}

export interface UITreeNode {
    id: string;
    name: string;
    kind: string;
    status: string;
    depth: number;

    // UI display info
    display_name: string;
    execution_time: string;
    event_count: number;

    // Domain context for filtering
    subdomain?: string;
    bounded_context?: string;
    entity?: string;

    // Events for expansion
    events: Array<{
        id: string;
        type: string;
        content: string;
        phase: string;
        timestamp: number;
        metadata: any;
    }>;

    // Recursive children
    children: UITreeNode[];

    // Collapsible state
    collapsed: boolean;

    // Performance info
    performance?: {
        total_time_ms: number;
        step_count: number;
        avg_step_time_ms: number;
    };
}

// ===== STREAM PROCESSOR SERVICE =====

@Injectable()
export class CognitiveStreamProcessorService {
    private current_execution: ResponseExecution | null = null;
    private span_registry: Map<string, SpanNode> = new Map();
    private event_sequence = 0;

    // Observable streams for UI components
    private executionSubject = new BehaviorSubject<ResponseExecution | null>(null);
    private cognitiveTreeSubject = new BehaviorSubject<SpanNode[]>([]);
    private responseContentSubject = new BehaviorSubject<string>('');
    private performanceSubject = new BehaviorSubject<any>(null);

    // Public observables
    public execution$ = this.executionSubject.asObservable();
    public cognitiveTree$ = this.cognitiveTreeSubject.asObservable();
    public responseContent$ = this.responseContentSubject.asObservable();
    public performance$ = this.performanceSubject.asObservable();

    constructor() {
        // console.log('🧠 CognitiveStreamProcessorService initialized');
    }

    /**
     * Process a single SSE event from the stream
     * ALL events come through as response.output_text.delta with custom_outputs
     */
    processSSEEvent(sseData: string): void {
        try {
            // Parse SSE data line
            if (!sseData.startsWith('data: ')) return;

            const jsonStr = sseData.slice(6); // Remove 'data: ' prefix
            if (jsonStr.trim() === '[DONE]') {
                this.completeCurrentExecution();
                return;
            }

            const event = JSON.parse(jsonStr);

            // ALL events come through as response.output_text.delta
            // We differentiate by looking at custom_outputs.event_type
            if (event.type === 'response.output_text.delta') {
                this.processStreamEvent(event);
            } else if (event.type === 'response.output_item.done') {
                this.completeCurrentExecution();
            }
        } catch (error) {
            console.error('Error processing SSE event:', error, 'Data:', sseData);
        }
    }

    /**
     * Process parsed event object directly (for integration with existing services)
     */
    processEventObject(event: any): void {
        if (!event) return;

        // Initialize execution if needed
        const customOutputs = event.custom_outputs || {};
        const eventType = customOutputs.event_type || '';

        // console.log('🔍 Processing event:', {
        //     type: event.type,
        //     event_type: eventType,
        //     has_delta: !!event.delta,
        //     delta_preview: event.delta?.substring(0, 50)
        // });

        if (!this.current_execution) {
            this.initializeExecution(customOutputs);
        }

        // Check the event_type in custom_outputs
        if (eventType.startsWith('craig.cognitive.')) {
            // console.log('🧠 Processing cognitive event:', eventType);
            this.processCognitiveEvent(event, customOutputs);
        } else if (eventType === 'response.llm.token') {
            // console.log('💬 Processing LLM token');
            this.processResponseToken(event);
        } else if (!eventType && event.delta) {
            // No event type but has delta - treat as response token
            // console.log('💬 Processing delta as response token (no event_type)');
            this.processResponseToken(event);
        }

        // Notify UI of updates
        this.updateObservables();
    }

    /**
     * Process a single stream event (cognitive or response)
     * Based on the actual format from example-stream.txt
     */
    private processStreamEvent(event: any): void {
        const customOutputs = event.custom_outputs || {};
        const eventType = customOutputs.event_type || '';

        // Initialize execution if needed
        if (!this.current_execution) {
            this.initializeExecution(customOutputs);
        }

        // Check the event_type in custom_outputs to determine what kind of event this is
        if (eventType.startsWith('craig.cognitive.')) {
            // This is a cognitive event (node start, tool execution, etc.)
            this.processCognitiveEvent(event, customOutputs);
        } else if (eventType === 'response.llm.token') {
            // This is an LLM response token
            this.processResponseToken(event);
        }
        // If no event_type, it might be a plain response token (backward compatibility)
        else if (!eventType && event.delta) {
            this.processResponseToken(event);
        }

        // Notify UI of updates
        this.updateObservables();
    }

    /**
     * Initialize a new response execution
     */
    private initializeExecution(customOutputs: any): void {
        const responseId = customOutputs.span?.response_id || `exec_${Date.now()}`;

        this.current_execution = {
            response_id: responseId,
            start_time: Date.now(),
            status: 'streaming',
            cognitive_tree: [],
            response_content: [],
            total_cognitive_time_ms: 0,
            total_events: 0,
            nodes_executed: [],
            tools_executed: []
        };

        this.span_registry.clear();
        this.event_sequence = 0;

        // console.log('🚀 New response execution started:', responseId);
        this.updateObservables();
    }

    /**
     * Process cognitive events and build span hierarchy
     * Based on actual event structure from example-stream.txt
     */
    private processCognitiveEvent(event: any, customOutputs: any): void {
        if (!this.current_execution) return;

        // Extract fields from custom_outputs (this is where all the data is)
        const spanInfo = customOutputs.span;
        const cognitiveInfo = customOutputs.cognitive || {};
        const domainInfo = customOutputs.domain;
        const nodeInfo = customOutputs.node;
        const toolInfo = customOutputs.tool_context;

        // Skip if no span info
        if (!spanInfo) {
            console.warn('Cognitive event missing span info:', customOutputs);
            return;
        }

        // Create cognitive event with the actual structure
        const cognitiveEvent: CognitiveEvent = {
            type: customOutputs.event_type,
            event_category: customOutputs.event_category || 'cognitive',
            schema_version: customOutputs.schema_version || 'craig.ddd.v3.0',
            timestamp: Date.now(),
            content: event.delta || '', // The delta contains the formatted message
            formatted_content: this.extractFormattedContent(event.delta),
            span: spanInfo,
            domain: domainInfo,
            cognitive: cognitiveInfo,
            node_context: nodeInfo,
            tool_context: toolInfo,
            chunk_id: event.chunk_id,
            item_id: event.item_id
        };

        // Get or create span node
        let spanNode = this.span_registry.get(spanInfo.span_id);

        if (!spanNode) {
            spanNode = this.createSpanNode(spanInfo, domainInfo);
            this.span_registry.set(spanInfo.span_id, spanNode);
            this.addSpanToHierarchy(spanNode);
        }

        // Add event to span
        spanNode.events.push(cognitiveEvent);
        spanNode.event_count = spanNode.events.length;

        // Update span timing and status
        this.updateSpanMetrics(spanNode, cognitiveEvent);

        // Update execution summary
        this.current_execution.total_events++;
        this.updateExecutionSummary(cognitiveEvent);

        // Track cognitive events in New Relic
        this.trackCognitiveEvent(cognitiveEvent, customOutputs);
    }

    /**
     * Process response tokens
     * These are the actual response text tokens that form the final answer
     */
    private processResponseToken(event: any): void {
        if (!this.current_execution) return;

        // Double-check this isn't a cognitive event
        const eventType = event.custom_outputs?.event_type || '';
        if (eventType.startsWith('craig.cognitive.')) {
            console.warn('Attempted to process cognitive event as response token:', eventType);
            return;
        }

        // Only process if we have actual content in delta
        if (!event.delta) {
            return;
        }

        const responseEvent: ResponseEvent = {
            type: 'response.llm.token',
            content: event.delta,
            chunk_id: event.chunk_id || 0,
            item_id: event.item_id || '',
            timestamp: Date.now()
        };

        this.current_execution.response_content.push(responseEvent);

        // Track response tokens in New Relic
        this.trackResponseToken(responseEvent);

        // console.log('📝 Response token:', event.delta);
    }

    /**
     * Create a new span node from span info
     */
    private createSpanNode(spanInfo: SpanInfo, domainInfo?: DomainContext): SpanNode {
        return {
            span_id: spanInfo.span_id,
            span_name: spanInfo.span_name,
            span_kind: spanInfo.span_kind,
            parent_span_id: spanInfo.parent_span_id,
            depth: spanInfo.depth,
            status: 'running',
            domain: domainInfo,
            events: [],
            children: [],
            event_count: 0,
            reasoning_steps: [],
            collapsed: true // Start collapsed
        };
    }

    /**
     * Add span to the correct position in hierarchy
     */
    private addSpanToHierarchy(spanNode: SpanNode): void {
        if (!this.current_execution) return;

        if (!spanNode.parent_span_id) {
            // Top-level span (Node)
            this.current_execution.cognitive_tree.push(spanNode);
        } else {
            // Child span (Tool) - find parent and add as child
            const parentSpan = this.span_registry.get(spanNode.parent_span_id);
            if (parentSpan) {
                parentSpan.children.push(spanNode);
            }
        }
    }

    /**
     * Update span metrics based on events
     */
    private updateSpanMetrics(spanNode: SpanNode, event: CognitiveEvent): void {
        // Track start time
        if (event.type.includes('start') && !spanNode.start_time) {
            spanNode.start_time = event.timestamp;
        }

        // Track completion
        if (event.type.includes('complete')) {
            spanNode.end_time = event.timestamp;
            spanNode.status = 'completed';
            spanNode.execution_time_ms =
                event.cognitive.execution_time_ms || (spanNode.start_time ? event.timestamp - spanNode.start_time : 0);
        }

        // Track reasoning steps
        if (event.cognitive.reasoning_step) {
            spanNode.reasoning_steps.push(event.formatted_content);
        }

        // Calculate performance summary
        if (spanNode.status === 'completed') {
            spanNode.performance_summary = {
                total_time_ms: spanNode.execution_time_ms || 0,
                step_count: spanNode.events.length,
                avg_step_time_ms: (spanNode.execution_time_ms || 0) / Math.max(spanNode.events.length, 1)
            };
        }
    }

    /**
     * Update execution-level summary
     */
    private updateExecutionSummary(event: CognitiveEvent): void {
        if (!this.current_execution) return;

        // Track nodes and tools executed
        if (event.span.span_kind === 'cognitive_node' && event.node_context) {
            const nodeName = event.node_context.node_name;
            if (!this.current_execution.nodes_executed.includes(nodeName)) {
                this.current_execution.nodes_executed.push(nodeName);
            }
        }

        if (event.span.span_kind === 'cognitive_tool' && event.tool_context) {
            const toolName = event.tool_context.tool_name;
            if (!this.current_execution.tools_executed.includes(toolName)) {
                this.current_execution.tools_executed.push(toolName);
            }
        }

        // Update total cognitive time
        if (event.cognitive.execution_time_ms) {
            this.current_execution.total_cognitive_time_ms = Math.max(
                this.current_execution.total_cognitive_time_ms,
                event.cognitive.execution_time_ms
            );
        }
    }

    /**
     * Complete the current execution
     */
    private completeCurrentExecution(): void {
        if (!this.current_execution) return;

        this.current_execution.status = 'completed';
        this.current_execution.end_time = Date.now();

        // Update all running spans to completed
        this.span_registry.forEach((span) => {
            if (span.status === 'running') {
                span.status = 'completed';
                span.end_time = Date.now();
            }
        });

        // console.log('✅ Response execution completed:', {
        //     total_time: this.current_execution.total_cognitive_time_ms,
        //     total_events: this.current_execution.total_events,
        //     nodes: this.current_execution.nodes_executed,
        //     tools: this.current_execution.tools_executed
        // });

        this.updateObservables();
    }

    /**
     * Update all observable streams
     */
    private updateObservables(): void {
        if (!this.current_execution) return;

        this.executionSubject.next(this.current_execution);
        this.cognitiveTreeSubject.next(this.current_execution.cognitive_tree);
        this.responseContentSubject.next(this.getResponseContent());
        this.performanceSubject.next(this.getPerformanceSummary());
    }

    /**
     * Extract clean formatted content from delta
     */
    private extractFormattedContent(delta: string): string {
        if (!delta) return '';
        // Remove the bracketed prefixes like "[SIGNAL_NODE_COGNITIVE_NODE] 🧠 "
        const match = delta.match(/^\[.*?\]\s*🧠\s*(.*)$/) || delta.match(/^\[.*?\]\s*🔧\s*(.*)$/);
        return match ? match[1] : delta;
    }

    // ===== PUBLIC API FOR UI =====

    /**
     * Get current execution state for UI rendering
     */
    getCurrentExecution(): ResponseExecution | null {
        return this.current_execution;
    }

    /**
     * Get collapsible tree structure for UI
     */
    getCognitiveTree(): SpanNode[] {
        return this.current_execution?.cognitive_tree || [];
    }

    /**
     * Get response content assembled from tokens
     */
    getResponseContent(): string {
        if (!this.current_execution) return '';

        return this.current_execution.response_content.map((event) => event.content).join('');
    }

    /**
     * Filter spans by domain (Signal/Insight/Executive)
     */
    getSpansByDomain(subdomain: string): SpanNode[] {
        const allSpans: SpanNode[] = [];

        const collectSpans = (spans: SpanNode[]) => {
            spans.forEach((span) => {
                if (span.domain?.subdomain === subdomain) {
                    allSpans.push(span);
                }
                collectSpans(span.children);
            });
        };

        collectSpans(this.getCognitiveTree());
        return allSpans;
    }

    /**
     * Filter spans by execution time (performance analysis)
     */
    getSlowSpans(minTimeMs: number = 1000): SpanNode[] {
        const slowSpans: SpanNode[] = [];

        const collectSlowSpans = (spans: SpanNode[]) => {
            spans.forEach((span) => {
                if ((span.execution_time_ms || 0) >= minTimeMs) {
                    slowSpans.push(span);
                }
                collectSlowSpans(span.children);
            });
        };

        collectSlowSpans(this.getCognitiveTree());
        return slowSpans.sort((a, b) => (b.execution_time_ms || 0) - (a.execution_time_ms || 0));
    }

    /**
     * Get Chain of Thought summary for a span
     */
    getChainOfThought(spanId: string): string[] {
        const span = this.span_registry.get(spanId);
        if (!span) return [];

        const thoughts: string[] = [];

        // Add span's reasoning steps
        thoughts.push(...span.reasoning_steps);

        // Add child tool reasoning steps
        span.children.forEach((child) => {
            thoughts.push(...child.reasoning_steps);
        });

        return thoughts;
    }

    /**
     * Get performance summary for UI dashboard
     */
    getPerformanceSummary(): any {
        if (!this.current_execution) return null;

        const nodePerformance = this.current_execution.cognitive_tree.map((node) => ({
            name: node.span_name,
            execution_time_ms: node.execution_time_ms || 0,
            event_count: node.event_count,
            tool_count: node.children.length,
            tools: node.children.map((tool) => ({
                name: tool.span_name,
                execution_time_ms: tool.execution_time_ms || 0,
                step_count: tool.event_count
            }))
        }));

        return {
            execution_id: this.current_execution.response_id,
            total_time_ms: this.current_execution.total_cognitive_time_ms,
            total_events: this.current_execution.total_events,
            nodes_executed: this.current_execution.nodes_executed.length,
            tools_executed: this.current_execution.tools_executed.length,
            node_performance: nodePerformance,
            response_length: this.getResponseContent().length
        };
    }

    /**
     * Generate tree structure for UI rendering (recursive)
     */
    generateUITree(): UITreeNode[] {
        const tree = this.getCognitiveTree().map((node) => this.spanToUINode(node));
        return tree;
    }

    private spanToUINode(span: SpanNode): UITreeNode {
        return {
            id: span.span_id,
            name: span.span_name,
            kind: span.span_kind,
            status: span.status,
            depth: span.depth,

            // UI display info
            display_name: this.getDisplayName(span),
            execution_time: span.execution_time_ms ? `${span.execution_time_ms.toFixed(1)}ms` : '',
            event_count: span.event_count,

            // Domain context for filtering
            subdomain: span.domain?.subdomain,
            bounded_context: span.domain?.bounded_context,
            entity: span.domain?.entity,

            // Events for expansion
            events: span.events.map((event) => ({
                id: `${event.span.span_id}_${event.chunk_id}`,
                type: event.type,
                content: event.formatted_content,
                phase: event.cognitive.cognitive_phase,
                timestamp: event.timestamp,
                metadata: event.cognitive
            })),

            // Recursive children
            children: span.children.map((child) => this.spanToUINode(child)),

            // Collapsible state
            collapsed: span.collapsed !== false, // Default to collapsed

            // Performance info
            performance: span.performance_summary
        };
    }

    private getDisplayName(span: SpanNode): string {
        const baseName = span.span_name
            .replace('_COGNITIVE_NODE', '')
            .replace('_COGNITIVE_TOOL', '')
            .replace('_', ' ')
            .toLowerCase();

        const timeInfo = span.execution_time_ms ? ` (${span.execution_time_ms.toFixed(1)}ms)` : '';
        const eventInfo = span.event_count > 0 ? `, ${span.event_count} events` : '';

        if (span.span_kind === 'cognitive_node') {
            return `🧠 ${baseName}${timeInfo}${eventInfo}`;
        } else if (span.span_kind === 'cognitive_tool') {
            return `🔧 ${baseName}${timeInfo}${eventInfo}`;
        } else {
            return `⚡ ${baseName}${timeInfo}${eventInfo}`;
        }
    }

    /**
     * Reset the processor for a new conversation
     */
    reset(): void {
        this.current_execution = null;
        this.span_registry.clear();
        this.event_sequence = 0;
        this.updateObservables();
        // console.log('🔄 CognitiveStreamProcessorService reset');
    }

    /**
     * Toggle span collapse state
     */
    toggleSpanCollapse(spanId: string): void {
        const span = this.span_registry.get(spanId);
        if (span) {
            span.collapsed = !span.collapsed;
            this.updateObservables();
        }
    }

    /**
     * Track cognitive events in New Relic for analysis
     */
    private trackCognitiveEvent(cognitiveEvent: CognitiveEvent, customOutputs: any): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const newrelic = (window as any).newrelic;

        if (newrelic && newrelic.addPageAction) {
            // Extract key information for tracking
            const eventType = cognitiveEvent.type;
            const spanInfo = cognitiveEvent.span;
            const domainInfo = cognitiveEvent.domain;
            const nodeInfo = cognitiveEvent.node_context;
            const toolInfo = cognitiveEvent.tool_context;

            // Determine event category for easier analysis
            let eventCategory = 'unknown';
            if (eventType.includes('thinking')) eventCategory = 'thinking';
            else if (eventType.includes('tool')) eventCategory = 'tool';
            else if (eventType.includes('response')) eventCategory = 'response';
            else if (eventType.includes('system')) eventCategory = 'system';

            newrelic.addPageAction('cognitive_event', {
                eventType: eventType,
                eventCategory: eventCategory,
                timestamp: new Date().toISOString(),
                sessionId: this.generateSessionId(),

                // Span information
                spanId: spanInfo?.span_id,
                spanName: spanInfo?.span_name,
                spanKind: spanInfo?.span_kind,

                // Domain information
                subdomain: domainInfo?.subdomain || 'unknown',
                boundedContext: domainInfo?.bounded_context || 'unknown',

                // Node/Tool context
                nodeName: nodeInfo?.node_name || 'unknown',
                toolName: toolInfo?.tool_name || 'unknown',

                // Content analysis
                contentLength: cognitiveEvent.content?.length || 0,
                hasContent: !!cognitiveEvent.content,
                contentPreview: cognitiveEvent.content?.substring(0, 100) || '',

                // Execution context
                executionId: this.current_execution?.response_id,
                totalEvents: this.current_execution?.total_events || 0,

                // Cognitive metadata
                executionTime: cognitiveEvent.cognitive?.execution_time_ms || 0,
                confidence: cognitiveEvent.cognitive?.['confidence'] || 0
            });
        }
    }

    /**
     * Track response tokens in New Relic for analysis
     */
    private trackResponseToken(responseEvent: ResponseEvent): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const newrelic = (window as any).newrelic;

        if (newrelic && newrelic.addPageAction) {
            newrelic.addPageAction('response_token', {
                eventType: responseEvent.type,
                timestamp: new Date().toISOString(),
                sessionId: this.generateSessionId(),

                // Token information
                chunkId: responseEvent.chunk_id,
                itemId: responseEvent.item_id,
                contentLength: responseEvent.content?.length || 0,
                hasContent: !!responseEvent.content,
                contentPreview: responseEvent.content?.substring(0, 50) || '',

                // Execution context
                executionId: this.current_execution?.response_id,
                totalTokens: this.current_execution?.response_content.length || 0,
                totalEvents: this.current_execution?.total_events || 0
            });
        }
    }

    /**
     * Generate a session ID for tracking user sessions
     */
    private generateSessionId(): string {
        // Try to get existing session ID from localStorage, or create a new one
        const existingSessionId = localStorage.getItem('craig_session_id');
        if (existingSessionId) {
            return existingSessionId;
        }

        const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('craig_session_id', sessionId);
        return sessionId;
    }
}
